import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'

import type { PuiCKEditorHandle } from '~/components/common/ckeditor/PuiCKEditor'
import FeatureToggle from '~/constants/featureToggle'
import {
  clearDischargeNotesInjection,
  clearMedicalNotesInjection,
  getPendingInjection,
} from '~/store/duck/audioRecorderInjection'
import type { RootState } from '~/store/index'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getSoapId } from '~/store/reducers/soap'

/**
 * Watches for pending audio summary injections and injects content
 * into the currently active CKEditor instance via the Model API.
 *
 * Injects whichever note type matches the active tab:
 *  - tab 0 → medical notes
 *  - tab 1 → discharge notes
 *
 * Each tab's content is cleared independently after injection,
 * so switching tabs auto-injects the other note type.
 *
 * Returns an `onEditorReady` callback — wire it into `editorReadyCallback`
 * so injection also triggers when the new editor finishes initializing
 * (covers the tab-switch case where the useEffect fires before the
 * new editor instance is mounted).
 *
 * Gated by AUDIO_RECORDER_SUMMARIES feature toggle.
 */
export const useAudioSummaryInjection = (
  editorRef: React.RefObject<PuiCKEditorHandle>,
  tabValue: number,
): { onEditorReady: () => void } => {
  const dispatch = useDispatch()
  const store = useStore()

  const summariesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AUDIO_RECORDER_SUMMARIES),
  )
  const pendingInjection = useSelector(getPendingInjection)

  const tryInject = useCallback(() => {
    // Read latest state directly — avoids stale closure issues
    const state = store.getState() as RootState
    const ftEnabled = getFeatureToggle(FeatureToggle.AUDIO_RECORDER_SUMMARIES)(
      state,
    )
    const pending = getPendingInjection(state)

    if (!ftEnabled) return
    if (!pending) return

    // Verify the pending injection is for the currently loaded SOAP.
    // If the SOAP reducer hasn't loaded yet (undefined/null), skip without
    // logging a mismatch — the injection stays pending and retries via
    // useEffect once the SOAP loads and pendingInjection is still present.
    const currentSoapId = getSoapId(state)
    if (currentSoapId == null) return

    if (pending.soapId !== currentSoapId) {
      // eslint-disable-next-line no-console
      console.warn(
        `[AudioSummaryInjection] soapId mismatch — pending: ${pending.soapId}, current: ${currentSoapId}`,
      )
      return
    }

    const isMedicalTab = tabValue === 0
    const content = isMedicalTab ? pending.medicalNotes : pending.dischargeNotes
    const noteLabel = isMedicalTab ? 'medical' : 'discharge'

    if (!content) return

    if (!editorRef.current) {
      // eslint-disable-next-line no-console
      console.warn(
        `[AudioSummaryInjection] Pending ${noteLabel} injection exists but editor ref is not available`,
      )
      return
    }

    // Inject via Model API — collaboration-safe, fires onChange automatically
    // Returns false if inner CKEditor instance isn't ready yet (tab-switch race)
    const injected = editorRef.current.replaceContent(content)

    if (!injected) {
      // Editor not ready yet — keep pending so onEditorReady can retry
      // eslint-disable-next-line no-console
      console.warn(
        `[AudioSummaryInjection] ${noteLabel} injection deferred — editor not ready`,
      )
      return
    }

    // Only clear after confirmed injection — keeps pending for onEditorReady retry
    dispatch(
      isMedicalTab
        ? clearMedicalNotesInjection()
        : clearDischargeNotesInjection(),
    )
  }, [tabValue, editorRef, dispatch, store])

  // Fires when pendingInjection arrives while editor is already mounted.
  // tryInject must be in the dependency array to avoid stale closures over
  // tabValue/editorRef — the early-return inside tryInject when !pending
  // makes extra invocations effectively free.
  useEffect(() => {
    if (!summariesEnabled || !pendingInjection) return
    tryInject()
  }, [summariesEnabled, pendingInjection, tryInject])

  // Returned callback — call from editorReadyCallback so injection runs
  // after the NEW editor instance is fully initialized on tab switch
  const onEditorReady = useCallback(() => {
    tryInject()
  }, [tryInject])

  // Note: stale pendingInjection (e.g. user closes SOAP without injecting)
  // is harmless — the soapId check prevents injection into the wrong SOAP,
  // and the data simply remains in Redux until a matching SOAP is opened or
  // a new setPendingInjection overwrites it. No explicit cleanup on
  // CLOSE_SOAP is needed because the cost is ~two nullable strings in memory.
  return { onEditorReady }
}
